'use client';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { DOMAIN_LABEL } from '@/src/docFields';
import { c } from '@/lib/vster/theme';
import Icon from '@/components/vster/Icon';

interface Props {
  loggedIn: boolean;
  filters: {
    q?: string; org?: string; cat1sub?: string; cat2?: string; region?: string; keyword?: string; domain?: string;
  };
}

export default function SaveAlertButton({ loggedIn, filters }: Props) {
  const router = useRouter();
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const [open, setOpen] = useState(false); // 모달 열림
  const [msg, setMsg] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState(true); // 이메일 알림 받기 속성(기본 on)

  // 활성 조건: 조건 2개 이상 선택 시에만 등록 가능(단일 조건은 매칭이 너무 넓어 알림 과다).
  const condCount = [filters.q, filters.org, filters.cat1sub, filters.cat2, filters.region, filters.keyword, filters.domain]
    .filter((v) => v && String(v).trim()).length;
  const hasCond = condCount >= 2;

  // 선택한 필터 조건으로 알림 이름 기본값 생성 (예: "AI·데이터 · 서울 · 창업").
  function buildDefaultName(): string {
    const parts: string[] = [];
    if (filters.domain) parts.push(DOMAIN_LABEL[filters.domain] ?? filters.domain);
    if (filters.cat2) parts.push(filters.cat2);
    if (filters.cat1sub) parts.push(filters.cat1sub);
    if (filters.region) parts.push(filters.region);
    if (filters.org) parts.push(filters.org);
    if (filters.keyword) parts.push(filters.keyword);
    if (filters.q) parts.push(filters.q);
    return parts.join(' · ');
  }

  function openModal() {
    if (!loggedIn) {
      router.push('/login?callbackUrl=' + encodeURIComponent(location.pathname + location.search));
      return;
    }
    if (!hasCond) return;
    setMsg(''); setName(buildDefaultName()); setEmail(true); setState('idle'); setOpen(true);
  }

  async function save() {
    setState('busy');
    const res = await fetch('/api/my/alerts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...filters, name: name.trim() || undefined, enabled: email }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setState('done');
      setMsg(email ? '알림을 등록했습니다. 매주 새 공고를 이메일로 받습니다.' : '알림을 등록했습니다.');
      setOpen(false);
    } else {
      setState('error');
      setMsg(data.error ?? '등록 실패');
    }
  }

  // 현재 필터 조건을 사람이 읽는 요약으로
  const condChips: string[] = [];
  if (filters.domain) condChips.push(DOMAIN_LABEL[filters.domain] ?? filters.domain);
  if (filters.region) condChips.push(filters.region);
  if (filters.cat2) condChips.push(filters.cat2);
  if (filters.org) condChips.push(`기관:${filters.org}`);
  if (filters.keyword) condChips.push(`#${filters.keyword}`);
  if (filters.q) condChips.push(`"${filters.q}"`);

  const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', border: '1px solid #c3c6d6', borderRadius: 9, fontSize: 15, background: '#f1f3ff', color: '#0E1726', boxSizing: 'border-box' };

  const done = state === 'done';
  return (
    <div>
      <button
        onClick={openModal}
        disabled={loggedIn && !hasCond}
        title={loggedIn && !hasCond ? '조건을 2개 이상 선택하세요' : ''}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          background: done ? c.ok : c.action, color: '#fff', border: 'none',
          cursor: loggedIn && !hasCond ? 'default' : 'pointer',
          padding: 12, borderRadius: 9, fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
          opacity: loggedIn && !hasCond ? 0.5 : 1,
        }}
      >
        <Icon name={done ? 'check' : 'notifications_active'} size={17} color="#fff" />
        {done ? '알림 등록됨' : '이 조건으로 알림 받기'}
      </button>
      {done && (
        <div style={{ fontSize: 12, color: c.muted, marginTop: 7, textAlign: 'center' }}>
          {msg} <a href="/workspace/recommend" style={{ color: c.actionDark, textDecoration: 'none', fontWeight: 600 }}>알림 관리 →</a>
        </div>
      )}
      {state === 'error' && !open && <div style={{ fontSize: 12, color: c.dangerText, marginTop: 7, textAlign: 'center' }}>{msg}</div>}
      {!loggedIn && state === 'idle' && (
        <div style={{ fontSize: 11.5, color: c.muted, marginTop: 6, textAlign: 'center' }}>로그인하면 매주 새 지원사업을 메일로 받아요</div>
      )}
      {loggedIn && !hasCond && state === 'idle' && (
        <div style={{ fontSize: 11.5, color: c.muted, marginTop: 6, textAlign: 'center' }}>조건을 2개 이상 선택하면 알림을 등록할 수 있어요</div>
      )}

      {/* 모달 — portal 로 document.body 에 렌더(상위 사이드바의 transform/stacking context 에 갇혀
           지원사업 목록 뒤로 깔리는 문제 방지). z-index 도 상향. */}
      {open && typeof document !== 'undefined' && createPortal(
        <div
          onClick={() => state !== 'busy' && setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(5,16,40,.45)', zIndex: 2147483000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 12px 40px rgba(5,16,40,.25)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#0E1726' }}>🔔 이 조건으로 알림 받기</h3>
              <button onClick={() => setOpen(false)} disabled={state === 'busy'} style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: '#9aa0b4', cursor: 'pointer' }}>×</button>
            </div>
            <p style={{ fontSize: 13, color: '#737685', margin: '0 0 16px' }}>등록한 조건에 맞는 새 지원사업이 올라오면 알려드려요.</p>

            {/* 조건 요약 */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#737685', marginBottom: 6 }}>검색 조건</div>
              {condChips.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {condChips.map((c, i) => (
                    <span key={i} style={{ fontSize: 12.5, fontWeight: 600, padding: '4px 10px', borderRadius: 999, background: '#E7EDFF', color: '#2C5CFF' }}>{c}</span>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#9aa0b4' }}>전체 지원사업</div>
              )}
            </div>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#434654', marginBottom: 6 }}>알림 이름</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 부산 AI 창업지원"
              maxLength={60}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && state !== 'busy') save(); }}
              style={{ ...inputStyle, marginBottom: 14 }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#434654', marginBottom: 18, cursor: 'pointer' }}>
              <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
              이메일로 매주 새 공고 받기
            </label>

            {state === 'error' && <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 12 }}>{msg}</div>}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setOpen(false)}
                disabled={state === 'busy'}
                style={{ flex: 1, background: '#fff', color: '#737685', border: '1px solid #d7dbe8', cursor: 'pointer', padding: '11px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}
              >
                취소
              </button>
              <button
                onClick={save}
                disabled={state === 'busy'}
                style={{ flex: 2, background: '#2C5CFF', color: '#fff', border: 'none', cursor: state === 'busy' ? 'default' : 'pointer', padding: '11px', borderRadius: 10, fontSize: 14, fontWeight: 700 }}
              >
                {state === 'busy' ? '등록 중…' : '알림 등록'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
